import * as React from 'react';
import {
  type ComponentPropsWithRef,
  type ReactNode,
  type RefAttributes,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  DialogProps as AriaDialogProps,
  ModalOverlayProps as AriaModalOverlayProps,
  ModalRenderProps as AriaModalRenderProps,
} from 'react-aria-components';
import {
  Dialog as AriaDialog,
  DialogTrigger as AriaDialogTrigger,
  Modal as AriaModal,
  ModalOverlay as AriaModalOverlay,
  OverlayTriggerStateContext,
} from 'react-aria-components';

import { StackingLayer, useStackingZIndex } from '@/contexts/StackingContext';
import { CloseButton } from '@/lib/base/buttons/close-button';
import { EdgeResizeHandle } from '@/lib/base/resize-handle/edge-resize-handle';
import { logger } from '@/lib/logger';
import { cx } from '@/utils/cx';
import { isThirdPartyEditorUi } from '@/utils/third-party-editor-ui';

import {
  computeSlideoutPanelStyle,
  NonModalSlideout,
  SLIDEOUT_ANIM_ENTER,
  SLIDEOUT_ANIM_ENTER_BOTTOM,
  SLIDEOUT_ANIM_EXIT,
  SLIDEOUT_ANIM_EXIT_BOTTOM,
  SLIDEOUT_DIALOG_INNER,
  SLIDEOUT_PANEL_BASE,
  SLIDEOUT_PANEL_BASE_BOTTOM,
  type SlideoutPlacement,
} from './slideout-menu-nonmodal';
import {
  SURFACE_DISMISS_ATTR,
  SurfaceDismissContext,
  type SurfaceDismissContextValue,
  useSurfaceDismiss,
} from './surface-dismiss-context';
import { UnsavedChangesModal } from './unsaved-changes-modal';
import { useSlideoutDismissGuard } from './use-slideout-dismiss-guard';

const noop = () => {};

interface ModalOverlayProps
  extends AriaModalOverlayProps, RefAttributes<HTMLDivElement> {
  /** Show a semi-transparent backdrop behind the slideout (default: true) */
  showBackdrop?: boolean;
}

/**
 * Returns true when the interact-outside event should NOT dismiss
 * the overlay. Checks three things:
 *
 * 1. The target is UI injected by a writing extension (Grammarly). That UI
 *    lives outside our React root, so it reads as "outside" and would close
 *    the panel mid-edit. See `@/utils/third-party-editor-ui`.
 * 2. The event target itself is inside a `[data-keep-overlay-open]`
 *    element (direct DOM ancestry).
 * 3. A `[data-keep-overlay-open]` element exists at the same screen
 *    coordinates. This handles the case where the overlay underlay
 *    (fixed inset-0) intercepts the click and becomes the event
 *    target, even though the user visually clicked on a floating
 *    panel that's rendered behind the underlay in the stacking context.
 */
function shouldKeepOverlayOpen(target: Element | EventTarget): boolean {
  if (isThirdPartyEditorUi(target)) {
    return true;
  }
  if (target instanceof Element && target.closest('[data-keep-overlay-open]')) {
    return true;
  }
  // Check if any element at the last known pointer position has the attribute.
  // We use the globally tracked coordinates because the event object passed to
  // shouldCloseOnInteractOutside doesn't carry coordinates.
  if (_lastPointerX !== null && _lastPointerY !== null) {
    const els = document.elementsFromPoint(_lastPointerX, _lastPointerY);
    return els.some(el => el.closest('[data-keep-overlay-open]') != null);
  }
  return false;
}

// Track the last pointer position so shouldKeepOverlayOpen can check
// elementsFromPoint even when the event target is the overlay underlay.
let _lastPointerX: number | null = null;
let _lastPointerY: number | null = null;
if (typeof document !== 'undefined') {
  document.addEventListener(
    'pointerdown',
    e => {
      _lastPointerX = e.clientX;
      _lastPointerY = e.clientY;
    },
    true
  );
}

export const ModalOverlay = ({
  showBackdrop = true,
  shouldCloseOnInteractOutside: callerShouldClose,
  ...props
}: ModalOverlayProps) => {
  const zIndex = useStackingZIndex();
  const { className: propsClassName } = props;
  const getClassName = useCallback(
    (state: AriaModalRenderProps & { defaultClassName: string | undefined }) =>
      cx(
        'fixed inset-0 flex min-h-[var(--app-viewport-height,100dvh)] w-full items-center justify-end outline-hidden',
        // Pass pointer events through the underlay so elements behind it
        // (e.g. the note compose panel) receive clicks. The Modal child
        // (the actual slideout panel) re-enables pointer-events.
        'pointer-events-none',
        // Own backdrop so stacked overlays each get a dimming layer.
        showBackdrop && 'bg-black/50',
        // Fade the backdrop in/out to prevent a flash of dark color
        state.isEntering && 'animate-in fade-in-0 duration-200',
        state.isExiting && 'animate-out fade-out-0 duration-500',
        typeof propsClassName === 'function'
          ? propsClassName(state)
          : propsClassName
      ),
    [showBackdrop, propsClassName]
  );
  const overlayStyle = useMemo(() => ({ zIndex }), [zIndex]);
  // Compose the internal compose-panel guard with any caller-provided
  // predicate: a click only dismisses when both agree. Without this,
  // consumers passing their own `shouldCloseOnInteractOutside` would have
  // it silently overridden.
  const shouldCloseOnInteractOutside = useCallback(
    (target: Element) => {
      if (shouldKeepOverlayOpen(target)) {
        return false;
      }
      return callerShouldClose ? callerShouldClose(target) : true;
    },
    [callerShouldClose]
  );
  return (
    <AriaModalOverlay
      {...props}
      shouldCloseOnInteractOutside={shouldCloseOnInteractOutside}
      className={getClassName}
      style={overlayStyle}
    />
  );
};
ModalOverlay.displayName = 'ModalOverlay';

interface ModalProps
  extends AriaModalOverlayProps, RefAttributes<HTMLDivElement> {
  /**
   * Custom width in pixels (overrides max-w-100). Interpreted as a height
   * instead when `placement='bottom'`.
   */
  width?: number;
  /** Whether resizing is currently in progress */
  isResizing?: boolean;
  /**
   * Panel placement. `'right'` (default) is the classic right-anchored
   * slideout. `'bottom'` anchors the panel to the bottom of the viewport —
   * pair with `showBackdrop={false}` on the overlay so content behind the
   * panel (e.g. a chart) stays visible and interactive.
   */
  placement?: SlideoutPlacement;
}

export const Modal = ({
  width,
  isResizing,
  placement = 'right',
  ...props
}: ModalProps) => {
  const zIndex = useStackingZIndex(10); // Slightly above overlay
  const { className: propsClassName } = props;
  const getClassName = useCallback(
    (state: AriaModalRenderProps & { defaultClassName: string | undefined }) =>
      cx(
        placement === 'bottom'
          ? SLIDEOUT_PANEL_BASE_BOTTOM
          : SLIDEOUT_PANEL_BASE,
        // Only apply the max size if not using a custom width/height
        !width && (placement === 'bottom' ? 'max-h-100' : 'max-w-100'),
        // Only animate when not resizing
        !isResizing &&
          (placement === 'bottom'
            ? SLIDEOUT_ANIM_ENTER_BOTTOM
            : SLIDEOUT_ANIM_ENTER),
        !isResizing &&
          (placement === 'bottom'
            ? SLIDEOUT_ANIM_EXIT_BOTTOM
            : SLIDEOUT_ANIM_EXIT),
        typeof propsClassName === 'function'
          ? propsClassName(state)
          : propsClassName
      ),
    [width, isResizing, placement, propsClassName]
  );
  const modalStyle = useMemo(
    () => computeSlideoutPanelStyle(zIndex, width, placement),
    [zIndex, width, placement]
  );
  return <AriaModal {...props} className={getClassName} style={modalStyle} />;
};
Modal.displayName = 'Modal';

interface DialogProps
  extends Omit<AriaDialogProps, 'children'>, RefAttributes<HTMLElement> {
  children?: ReactNode | ((opts: { close: () => void }) => ReactNode);
}

export const Dialog = ({ children, ...props }: DialogProps) => (
  <AriaDialog
    role='dialog'
    aria-label='Slideout panel'
    {...props}
    className={cx(
      // Outer panel must NOT scroll. SlideoutMenu.Content is the single
      // scroll container; the Dialog stays overflow-clip so the Header
      // and Footer stay pinned and the inner Content owns the scrollbar.
      // Previously had overflow-y-auto here, producing nested scroll
      // containers (Dialog + Content) — caused header/footer to scroll
      // out of view and ambiguous wheel targeting on long forms like the
      // booking-link create slideout.
      //
      // Uses `overflow-clip` (not `overflow-hidden`): both clip the visual
      // overflow identically, but `hidden` makes the element a scroll
      // container which `Element.scrollIntoView()` from a deep descendant
      // (or `el.scrollTop = X` from any other code) can still scroll —
      // hiding the Header/Content/Footer "above" the visible area even
      // though no scrollbar appears. `overflow-clip` is not a scroll
      // container, so the same scroll-into-view call walks up to the
      // SlideoutMenu.Content `overflow-y-auto` region (the intended
      // scroller) instead of silently breaking the layout. Reproduced
      // historically by toggling Saturday/Sunday checkboxes in the
      // working-hours field on the booking-link create slideout, which
      // remounted enough subtree to trigger a focus-driven scrollIntoView
      // and left the entire panel blank.
      SLIDEOUT_DIALOG_INNER,
      props.className
    )}
  >
    {({ close }) => (
      <StackingLayer>
        {typeof children === 'function' ? children({ close }) : children}
      </StackingLayer>
    )}
  </AriaDialog>
);
Dialog.displayName = 'Dialog';

/**
 * Default initial width for any slideout. All slideouts share this so the
 * product feels consistent — opening a proposal, a CRM record, a filter
 * builder, or a settings slideout always lands at the same width unless
 * the consumer has a specific reason to override it. Resize state inside
 * a single slideout instance is preserved separately (via `onWidthChange`
 * + a context like `SlideoutWidthContext` or `ActionSidebarContext`).
 */
export const DEFAULT_SLIDEOUT_WIDTH = 600;

interface SlideoutMenuProps
  extends
    Omit<AriaModalOverlayProps, 'children'>,
    RefAttributes<HTMLDivElement> {
  children:
    | ReactNode
    | ((children: AriaModalRenderProps & { close: () => void }) => ReactNode);
  dialogClassName?: string;
  /**
   * Accessible label for the inner Dialog. Defaults to "Slideout panel".
   * Set this to something specific (e.g. "Email Thread", "Filter Builder")
   * so screen readers announce the right context when the slideout opens.
   */
  dialogAriaLabel?: string;
  /** Enable resize functionality */
  resizable?: boolean;
  /**
   * Panel placement. `'right'` (default) anchors the panel to the right
   * edge of the viewport with a left-edge resize handle. `'bottom'` anchors
   * it to the bottom edge instead, with a top-edge resize handle — pair with
   * `showBackdrop={false}` so content behind the panel stays visible and
   * interactive (e.g. a chart the panel drills into). `initialWidth` /
   * `minWidth` / `maxWidth` below are reinterpreted as height in this mode.
   */
  placement?: SlideoutPlacement;
  /**
   * Initial width in pixels (default: `DEFAULT_SLIDEOUT_WIDTH`). Interpreted
   * as an initial height when `placement='bottom'`.
   */
  initialWidth?: number;
  /**
   * Minimum width in pixels (default: 320). Interpreted as a minimum height
   * when `placement='bottom'`.
   */
  minWidth?: number;
  /**
   * Maximum width in pixels (default: 800). Interpreted as a maximum height
   * when `placement='bottom'`.
   */
  maxWidth?: number;
  /** Callback when width changes */
  onWidthChange?: (width: number) => void;
  /**
   * Called when a resize drag starts (`true`) and ends (`false`).
   *
   * `onWidthChange` alone is not enough for a consumer that animates against
   * this panel's width: it fires on every mousemove, so a CSS transition on the
   * consumer's side restarts ~60×/sec and visibly lags the drag. Knowing a drag
   * is in progress lets the consumer suppress its transition and track the
   * pointer 1:1, then restore it on release. `ActionSidebarContext` exposes the
   * same signal for the proposal sidebar.
   *
   * MUST be a stable reference (a `useState` setter or `useCallback`) — it is
   * an effect dependency here.
   */
  onResizingChange?: (isResizing: boolean) => void;
  /** Show a semi-transparent backdrop behind the slideout (default: true) */
  showBackdrop?: boolean;
  /**
   * Optional header content. When provided, the slideout switches to
   * layout-managed mode: header is rendered in a pinned <Header> slot at
   * the top with a built-in close button, `children` is auto-wrapped in
   * a scrollable <Content> region, and `footer` (if provided) is pinned
   * at the bottom.
   *
   * This is the recommended API for any slideout with a static header /
   * footer + scrollable body (e.g. forms). It guarantees the
   * single-scroll-container invariant by construction — consumers cannot
   * accidentally introduce nested scroll regions.
   *
   * Omit `header` AND `footer` to fall back to manual mode (children
   * render directly inside the Dialog with no auto-layout). Manual mode
   * supports the render-prop `({ close }) => …` form and is for
   * SINGLE-STATE layouts that compose `SlideoutMenu.Header` / `.Content` /
   * `.Footer` themselves. Content with more than one state (loading /
   * error / ready) MUST use the slots: a close button rendered inside one
   * content branch does not exist in the others, and the user has no way
   * out of those states. The chrome owns the exits — see
   * docs/claude/REACT_19_PATTERNS.md § 1.
   *
   * Manual-mode scroll contract: the Dialog wrapper is `overflow-clip`
   * to enforce the single-scroll-container invariant (so a scrollable
   * Header / Footer never end up nested inside another scrollable area).
   * Consumers in manual mode MUST therefore wrap any content that can
   * exceed the viewport in `<SlideoutMenu.Content>` (or another
   * `overflow-y-auto` region) — otherwise tall content will be clipped
   * with no way to scroll. Existing manual-mode consumers already follow
   * this pattern.
   */
  header?: ReactNode;
  /**
   * Optional footer content (typically action buttons). See `header`
   * docs for layout-managed mode semantics. Either prop opts in.
   */
  footer?: ReactNode;
  /** Optional className applied to the auto-wrapped Content scroll region. */
  contentClassName?: string;
  /**
   * Optional className applied to the layout-managed Header region — for
   * consumers whose header rhythm differs from the default `px-4 pt-6 pb-6`.
   * Merged with `cx`, so a `py-3` here replaces the default vertical padding.
   */
  headerClassName?: string;
  /** Optional className applied to the layout-managed Footer region. */
  footerClassName?: string;
  /**
   * Layout-managed mode: right-aligned controls for the Header row, rendered
   * before its close button. See `SlideoutMenu.Header`'s `actions`.
   */
  headerActions?: ReactNode;
  /** Layout-managed mode: the Header row's vertical alignment. Default `center`. */
  headerAlign?: 'start' | 'center';
  /**
   * Layout-managed mode: content rendered full-width beneath the header's
   * first row. Forwarded to `SlideoutMenu.Header`'s `secondRow`.
   */
  headerSecondRow?: ReactNode;
  /**
   * Layout-managed mode: render the Header's close button. Default `true`;
   * the chrome owns the exit, so only opt out when the host renders one
   * elsewhere in every state.
   */
  showCloseButton?: boolean;
  /**
   * Optional "go back" for hosts that stack surfaces (the CRM slideout stack).
   * Published on `SurfaceDismissContext` as `back` so a header can render a
   * back chevron without the host threading it through props. The chrome
   * renders no back affordance itself — only the header knows where it goes.
   */
  onBack?: () => void;
  /**
   * When defined, the chrome owns unsaved-changes dismissal: outside-click /
   * Escape / close prompt via UnsavedChangesModal while true. Absent ⇒ no guard.
   */
  hasUnsavedChanges?: boolean;
  /** Called when the user confirms they want to discard unsaved changes. */
  onDiscardUnsavedChanges?: () => void;
  /** Called when the user confirms they want to save before leaving. Returning a rejected promise keeps the modal open. */
  onSaveUnsavedChanges?: () => Promise<void>;
  /** When true, attaches a beforeunload handler to block browser close while dirty. */
  blockBrowserCloseWhileDirty?: boolean;
  /** Optional test-handle instance, surfaced as data-instance on the rendered Dialog. */
  instance?: string;
  /**
   * Opt-in non-modal mode for surfaces that must leave the page behind fully
   * interactive; only the CRM record slideout uses it. When true, renders via
   * portal without modal machinery (no inert, no scroll-lock, no focus-trap,
   * no backdrop, no outside-click dismissal).
   */
  nonModal?: boolean;
}

function resolveChildren(
  children: SlideoutMenuProps['children'],
  state: AriaModalRenderProps,
  close: () => void
): ReactNode {
  return typeof children === 'function'
    ? children({ ...state, close })
    : children;
}

// A ReactNode counts as "provided" for layout-managed opt-in only when it
// could actually render content. `null`, `undefined`, and booleans (the
// values React renders as nothing) do NOT count — otherwise a conditional
// like `header={cond && <Foo />}` would silently flip into layout-managed
// mode and render empty Header chrome with a close button when `cond` is
// false.
function isRenderableNode(node: ReactNode): boolean {
  return node !== undefined && node !== null && typeof node !== 'boolean';
}

interface SlideoutBodyProps {
  header: ReactNode;
  footer: ReactNode;
  contentClassName: string | undefined;
  headerClassName: string | undefined;
  footerClassName: string | undefined;
  headerActions: ReactNode;
  headerAlign: 'start' | 'center' | undefined;
  headerSecondRow: ReactNode;
  showCloseButton: boolean | undefined;
  children: SlideoutMenuProps['children'];
  state: AriaModalRenderProps;
  close: () => void;
}

/**
 * Renders the layout-managed body: pinned Header, scrollable Content,
 * pinned Footer. Only mounted when the consumer opted in by passing
 * `header` or `footer` to SlideoutMenu — manual mode bypasses this and
 * inlines the consumer's children directly inside the Dialog.
 */
function SlideoutBody({
  header,
  footer,
  contentClassName,
  headerClassName,
  footerClassName,
  headerActions,
  headerAlign,
  headerSecondRow,
  showCloseButton,
  children,
  state,
  close,
}: SlideoutBodyProps) {
  return (
    <>
      {isRenderableNode(header) && (
        <Header
          onClose={close}
          className={headerClassName}
          actions={headerActions}
          align={headerAlign}
          secondRow={headerSecondRow}
          showCloseButton={showCloseButton}
        >
          {header}
        </Header>
      )}
      <Content className={contentClassName}>
        {resolveChildren(children, state, close)}
      </Content>
      {isRenderableNode(footer) && (
        <Footer className={footerClassName}>{footer}</Footer>
      )}
    </>
  );
}
SlideoutBody.displayName = 'SlideoutBody';

// Instances already warned about, so a surface that stays open across many
// re-renders (or re-opens in a session) reports its missing exit once.
const dismissWarnedInstances = new Set<string>();

interface SlideoutInnerProps {
  /** Supplies isEntering/isExiting for the ready marker. */
  state: AriaModalRenderProps;
  close: () => void;
  onBack: (() => void) | undefined;
  instance: string | undefined;
  resizable: boolean;
  resizeHandleProps: {
    onResizeStart: (e: React.MouseEvent) => void;
    onKeyboardResize: (delta: number) => void;
    width: number;
    minWidth: number;
    maxWidth: number;
    isResizing: boolean;
    edge: 'left' | 'top';
    'aria-label': string;
  } | null;
  isLayoutManaged: boolean;
  header: ReactNode;
  footer: ReactNode;
  contentClassName: string | undefined;
  headerClassName: string | undefined;
  footerClassName: string | undefined;
  headerActions: ReactNode;
  headerAlign: 'start' | 'center' | undefined;
  headerSecondRow: ReactNode;
  showCloseButton: boolean | undefined;
  children: SlideoutMenuProps['children'];
}

/**
 * Shared Dialog inner render body: the E2E ready marker, the optional
 * resize handle, the `SurfaceDismissContext` provider, the development-mode
 * "has an exit" assertion, and the layout-managed/manual body split. Shared
 * by the modal and non-modal paths so the dismissal contract cannot diverge
 * between them.
 */
function SlideoutInner({
  state,
  close,
  onBack,
  instance,
  resizable,
  resizeHandleProps,
  isLayoutManaged,
  header,
  footer,
  contentClassName,
  headerClassName,
  footerClassName,
  headerActions,
  headerAlign,
  headerSecondRow,
  showCloseButton,
  children,
}: SlideoutInnerProps) {
  const isReady = !state.isEntering && !state.isExiting;

  const dismissValue = useMemo<SurfaceDismissContextValue>(
    () => ({
      close,
      back: onBack,
      isEntering: state.isEntering,
      isExiting: state.isExiting,
    }),
    [close, onBack, state.isEntering, state.isExiting]
  );

  // Development-mode assertion of the invariant this primitive exists to
  // uphold: an open surface has a way out in EVERY state, not only the one
  // whose content happens to render a close button. The chrome's own close
  // carries `SURFACE_DISMISS_ATTR`; a manual-mode consumer that renders its
  // own dismiss control must carry it too. Checked once the enter animation
  // has finished (content may still be loading — that is exactly the state
  // this catches). Never runs in production or tests; the test-side check is
  // `expectSurfaceDismissible` in `@/test/surface-utils`.
  const markerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development' || !isReady) {
      return;
    }
    const dialog = markerRef.current?.closest('[role="dialog"]');
    if (!dialog || dialog.querySelector(`[${SURFACE_DISMISS_ATTR}]`)) {
      return;
    }
    const key = instance ?? '(no instance)';
    if (dismissWarnedInstances.has(key)) {
      return;
    }
    dismissWarnedInstances.add(key);
    logger.warn(
      `SlideoutMenu: open slideout "${key}" has no dismiss control. Render the ` +
        `header/footer through the \`header\`/\`footer\` slots so the chrome owns ` +
        `the close, or mark your own control with ${SURFACE_DISMISS_ATTR}. ` +
        'See docs/claude/REACT_19_PATTERNS.md § 1.',
      { instance: key }
    );
  }, [isReady, instance]);

  return (
    <SurfaceDismissContext value={dismissValue}>
      {/* Hidden marker for E2E tests to detect transition completion */}
      <div
        ref={markerRef}
        data-testid='slideout-ready-marker'
        data-component='slideout-ready-marker'
        aria-hidden='true'
        style={{ display: 'none' }}
        data-ready={isReady ? '' : undefined}
      />
      {resizable && resizeHandleProps && (
        <EdgeResizeHandle {...resizeHandleProps} />
      )}
      {isLayoutManaged ? (
        <SlideoutBody
          header={header}
          footer={footer}
          contentClassName={contentClassName}
          headerClassName={headerClassName}
          footerClassName={footerClassName}
          headerActions={headerActions}
          headerAlign={headerAlign}
          headerSecondRow={headerSecondRow}
          showCloseButton={showCloseButton}
          state={state}
          close={close}
        >
          {children}
        </SlideoutBody>
      ) : (
        resolveChildren(children, state, close)
      )}
    </SurfaceDismissContext>
  );
}
SlideoutInner.displayName = 'SlideoutInner';

const Menu = ({
  children,
  dialogClassName,
  dialogAriaLabel,
  resizable = false,
  placement = 'right',
  initialWidth = DEFAULT_SLIDEOUT_WIDTH,
  minWidth = 320,
  maxWidth = 800,
  onWidthChange,
  onResizingChange,
  isDismissable = true,
  showBackdrop = true,
  header,
  footer,
  contentClassName,
  headerClassName,
  footerClassName,
  headerActions,
  headerAlign,
  headerSecondRow,
  showCloseButton,
  onBack,
  hasUnsavedChanges,
  onDiscardUnsavedChanges,
  onSaveUnsavedChanges,
  blockBrowserCloseWhileDirty,
  instance,
  nonModal = false,
  onOpenChange: consumerOnOpenChange,
  ...props
}: SlideoutMenuProps) => {
  // Layout-managed mode: opted in by passing a renderable `header` and/or
  // `footer`. The slideout owns the Header → Content → Footer composition
  // so the single-scroll-container invariant cannot be broken by consumers.
  // Booleans / null are excluded so `header={cond && <Foo />}` falls back
  // to manual mode when `cond` is false instead of rendering empty chrome.
  const isLayoutManaged = isRenderableNode(header) || isRenderableNode(footer);
  // Resize state
  const [width, setWidth] = useState(initialWidth);
  // Adopt a changed `initialWidth` (consumers feed a persisted/context width
  // back in) during render, not in an effect: a prop-to-state sync effect
  // paints one stale frame first (REACT_19_PATTERNS § 4.2). Comparing against
  // the previous prop is the sanctioned setter-in-render, and it only fires
  // when the prop actually changes — a drag that echoes `onWidthChange` back
  // as `initialWidth` lands on the value `width` already holds.
  const [prevInitialWidth, setPrevInitialWidth] = useState(initialWidth);
  if (initialWidth !== prevInitialWidth) {
    setPrevInitialWidth(initialWidth);
    setWidth(initialWidth);
  }
  const [isResizing, setIsResizing] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartWidth, setDragStartWidth] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  // Height follows the VISUAL viewport (window.visualViewport.height) — the
  // same measurement `--app-viewport-height` caps a bottom panel's rendered
  // height with (see computeSlideoutPanelStyle). On mobile the visual viewport
  // is shorter than window.innerHeight while the URL/toolbar chrome or the
  // on-screen keyboard is showing; measuring from innerHeight would let drag,
  // keyboard resize, and aria-valuemax advertise a height the CSS immediately
  // caps away.
  const [viewportHeight, setViewportHeight] = useState(() =>
    typeof window === 'undefined'
      ? 768
      : (window.visualViewport?.height ?? window.innerHeight)
  );
  const rafIdRef = useRef<number | null>(null);
  // `placement='bottom'` is only implemented for the modal render path; the
  // non-modal slideout renders right-anchored (see the dev-warning effect
  // below). Excluding `nonModal` here keeps the resize axis/handle/bounds
  // consistent with the right-anchored panel that actually renders.
  const isBottom = placement === 'bottom' && !nonModal;

  // Drag and keyboard resize both cap at the viewport, so a viewport narrower
  // than maxWidth lowers the reachable maximum — and the minimum can never
  // exceed that maximum, or a minWidth larger than the viewport would pin the
  // panel and leave it un-shrinkable. Compute both bounds ONCE so the drag
  // handler, the keyboard handler, and the aria-value props all clamp to the
  // same reachable range.
  const effectiveViewport = isBottom ? viewportHeight : viewportWidth;
  const effectiveMaxWidth = Math.min(maxWidth, effectiveViewport);
  const effectiveMinWidth = Math.min(minWidth, effectiveMaxWidth);

  // Handle resize start. For a bottom-anchored panel, the drag pointer
  // tracks the Y axis (dragging the top-edge handle up grows height)
  // instead of X.
  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setDragStartX(isBottom ? e.clientY : e.clientX);
      setDragStartWidth(width);
      setIsResizing(true);
      document.body.style.cursor = isBottom ? 'row-resize' : 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [width, isBottom]
  );

  // Track viewport dimension changes. Width follows the layout viewport
  // (window.innerWidth) to match the right panel's `100vw` cap; height follows
  // the VISUAL viewport (see the viewportHeight note above) to match the bottom
  // panel's `--app-viewport-height` cap, so the resize bounds never advertise a
  // height the CSS immediately clamps away. The visualViewport `scroll` event
  // fires as mobile browser chrome retracts/expands, changing the visible
  // height without a `resize`.
  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
      setViewportHeight(window.visualViewport?.height ?? window.innerHeight);
    };

    const { visualViewport } = window;
    window.addEventListener('resize', handleResize);
    visualViewport?.addEventListener('resize', handleResize);
    visualViewport?.addEventListener('scroll', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      visualViewport?.removeEventListener('resize', handleResize);
      visualViewport?.removeEventListener('scroll', handleResize);
    };
  }, []);

  // Reject the unsupported `nonModal` + `placement='bottom'` combination
  // explicitly: NonModalSlideout renders right-anchored, so `isBottom` above
  // already forces the resize logic back to the right axis. Warn in development
  // so a caller sees the mismatch rather than shipping a bottom-configured panel
  // that silently renders on the right.
  useEffect(() => {
    if (
      process.env.NODE_ENV === 'development' &&
      nonModal &&
      placement === 'bottom'
    ) {
      logger.warn(
        "SlideoutMenu: placement='bottom' is not supported together with " +
          'nonModal. The panel renders right-anchored; use the modal path ' +
          '(omit nonModal) for a bottom-anchored slideout.'
      );
    }
  }, [nonModal, placement]);

  // Handle keyboard resize (arrow keys). The sign of `delta` already
  // encodes "grow"/"shrink" correctly for the active edge (EdgeResizeHandle
  // mirrors its key semantics between the `left`/`top` edges), so this just
  // clamps against the axis-appropriate viewport dimension.
  const handleKeyboardResize = useCallback(
    (delta: number) => {
      const newWidth = Math.min(
        Math.max(width + delta, effectiveMinWidth),
        effectiveMaxWidth
      );
      setWidth(newWidth);
      onWidthChange?.(newWidth);
    },
    [width, effectiveMinWidth, effectiveMaxWidth, onWidthChange]
  );

  // Handle mouse move and mouse up during resize
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) {
        return;
      }

      // Calculate new size. Dragging the handle toward the panel's interior
      // increases size: left-edge handle grows on drag-left (dragStartX -
      // clientX), top-edge handle grows on drag-up (dragStartX - clientY,
      // since dragStartX holds the Y coordinate captured at drag start when
      // isBottom).
      const currentCoord = isBottom ? e.clientY : e.clientX;
      const deltaCoord = dragStartX - currentCoord;
      const newWidth = Math.min(
        Math.max(dragStartWidth + deltaCoord, effectiveMinWidth),
        effectiveMaxWidth
      );

      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
      rafIdRef.current = requestAnimationFrame(() => {
        setWidth(newWidth);
        onWidthChange?.(newWidth);
      });
    };

    const handleMouseUp = () => {
      if (isResizing) {
        setIsResizing(false);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      // Clean up body styles in case component unmounts during resize
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [
    isResizing,
    dragStartX,
    dragStartWidth,
    effectiveMinWidth,
    effectiveMaxWidth,
    isBottom,
    onWidthChange,
  ]);

  // Report drag start/end to the consumer. The cleanup covers unmounting
  // mid-drag, which would otherwise strand the consumer in a "resizing" state
  // forever — with its transition permanently suppressed.
  useEffect(() => {
    onResizingChange?.(isResizing);
    if (!isResizing) {
      return;
    }
    return () => onResizingChange?.(false);
  }, [isResizing, onResizingChange]);

  const resizeHandleProps = useMemo(() => {
    if (!resizable) {
      return null;
    }
    // Announce the same reachable range the drag/keyboard handlers clamp to
    // (effectiveMinWidth/effectiveMaxWidth), so assistive tech never hears a
    // resize range the panel can't hit.
    return {
      onResizeStart: handleResizeStart,
      onKeyboardResize: handleKeyboardResize,
      // Clamp the announced value to the reachable maximum. An initial
      // height taller than the viewport (a bottom panel opened with
      // initialWidth > effectiveMaxWidth) would otherwise emit an
      // aria-valuenow above aria-valuemax, and start the handle out of sync
      // with the panel the browser actually caps at the viewport.
      width: Math.min(width, effectiveMaxWidth),
      minWidth: effectiveMinWidth,
      maxWidth: effectiveMaxWidth,
      isResizing,
      edge: (isBottom ? 'top' : 'left') as 'left' | 'top',
      'aria-label': isBottom
        ? 'Resize slideout panel. Use up and down arrow keys to resize.'
        : 'Resize slideout panel. Use left and right arrow keys to resize.',
    };
  }, [
    resizable,
    handleResizeStart,
    handleKeyboardResize,
    width,
    effectiveMinWidth,
    effectiveMaxWidth,
    isBottom,
    isResizing,
  ]);

  // Mirror what NonModalSlideout does internally: fall back to the
  // OverlayTriggerStateContext (from SlideoutMenu.Trigger) when no explicit
  // onOpenChange prop was provided. This lets the guard intercept trigger-managed
  // closes too (e.g. Escape in non-modal mode with a trigger).
  const triggerCtx = useContext(OverlayTriggerStateContext);
  const effectiveOnOpenChange =
    consumerOnOpenChange ?? triggerCtx?.setOpen ?? undefined;

  // Strict undefined check — protects non-guard consumers (31 others) from
  // any truthiness coercion when hasUnsavedChanges is not passed at all.
  const guardActive = hasUnsavedChanges !== undefined;
  const { guard, modalProps } = useSlideoutDismissGuard({
    hasUnsavedChanges: hasUnsavedChanges ?? false,
    onDiscard: onDiscardUnsavedChanges ?? noop,
    onSave: onSaveUnsavedChanges,
    blockBrowserClose: blockBrowserCloseWhileDirty ?? false,
  });
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open || !guardActive) {
        effectiveOnOpenChange?.(open);
        return;
      }
      if (guard(() => effectiveOnOpenChange?.(false))) {
        effectiveOnOpenChange?.(false);
      }
    },
    [guardActive, guard, effectiveOnOpenChange]
  );

  if (nonModal) {
    return (
      <>
        {guardActive && <UnsavedChangesModal {...modalProps} />}
        <NonModalSlideout
          panelClassName={props.className}
          dialogClassName={dialogClassName}
          dialogAriaLabel={dialogAriaLabel}
          instance={instance}
          isOpen={props.isOpen}
          onOpenChange={handleOpenChange}
          isKeyboardDismissDisabled={props.isKeyboardDismissDisabled}
          width={resizable ? width : undefined}
          isResizing={isResizing}
          renderBody={(state, close) => (
            <SlideoutInner
              state={state}
              close={close}
              onBack={onBack}
              instance={instance}
              resizable={resizable}
              resizeHandleProps={resizeHandleProps}
              isLayoutManaged={isLayoutManaged}
              header={header}
              footer={footer}
              contentClassName={contentClassName}
              headerClassName={headerClassName}
              footerClassName={footerClassName}
              headerActions={headerActions}
              headerAlign={headerAlign}
              headerSecondRow={headerSecondRow}
              showCloseButton={showCloseButton}
            >
              {children}
            </SlideoutInner>
          )}
        />
      </>
    );
  }

  return (
    <>
      {guardActive && <UnsavedChangesModal {...modalProps} />}
      <ModalOverlay
        isDismissable={isDismissable}
        showBackdrop={showBackdrop}
        onOpenChange={handleOpenChange}
        {...props}
      >
        <Modal
          className={state =>
            cx(
              typeof props.className === 'function'
                ? props.className(state)
                : props.className
            )
          }
          width={resizable ? width : undefined}
          isResizing={isResizing}
          placement={placement}
        >
          {state => (
            <Dialog
              className={cx('relative', dialogClassName)}
              {...(dialogAriaLabel?.trim()
                ? { 'aria-label': dialogAriaLabel }
                : {})}
              data-component='slideout'
              data-instance={instance}
            >
              {({ close }) => (
                <SlideoutInner
                  state={state}
                  close={close}
                  onBack={onBack}
                  instance={instance}
                  resizable={resizable}
                  resizeHandleProps={resizeHandleProps}
                  isLayoutManaged={isLayoutManaged}
                  header={header}
                  footer={footer}
                  contentClassName={contentClassName}
                  headerClassName={headerClassName}
                  footerClassName={footerClassName}
                  headerActions={headerActions}
                  headerAlign={headerAlign}
                  headerSecondRow={headerSecondRow}
                  showCloseButton={showCloseButton}
                >
                  {children}
                </SlideoutInner>
              )}
            </Dialog>
          )}
        </Modal>
      </ModalOverlay>
    </>
  );
};
Menu.displayName = 'SlideoutMenu';

const Content = (props: ComponentPropsWithRef<'div'>) => {
  return (
    <div
      {...props}
      className={cx(
        'flex size-full flex-col overflow-y-auto overscroll-auto px-4 md:px-6',
        props.className
      )}
    />
  );
};
Content.displayName = 'SlideoutContent';

interface SlideoutHeaderProps extends ComponentPropsWithRef<'header'> {
  /**
   * Close handler for the built-in close button. Optional: inside a
   * `SlideoutMenu` it falls back to the surface's own `close` from
   * `SurfaceDismissContext`, so manual-mode consumers no longer need to
   * capture `close` from the render prop just to hand it back here.
   */
  onClose?: () => void;
  /**
   * Right-aligned controls (icon buttons, CTAs, an overflow menu). Rendered
   * in one cluster with the close button as its last item, so every header
   * ends the same way: … actions, close.
   */
  actions?: ReactNode;
  /** Render the close button at the end of the row. Default `true`. */
  showCloseButton?: boolean;
  /**
   * Vertical alignment of the first row. `center` (default) suits a one- or
   * two-line title; `start` suits a `secondRow` layout, aligning the actions
   * cluster to the top of the first (chip) row.
   */
  align?: 'start' | 'center';
  /**
   * Optional full-width second row. When provided, the header renders TWO
   * rows: `children` beside the `actions`+close cluster on the first row,
   * and `secondRow` at full width beneath. Absent (the default), the header
   * renders its current single-row layout, unchanged.
   */
  secondRow?: ReactNode;
}

/**
 * The slideout's header row: `children` on the left, an optional `actions`
 * cluster on the right, and the close button rendered INLINE at the end of
 * that cluster — never floating over the content. The close is on by
 * default; a host that frames content without a way out of its own (an
 * inline review panel) opts out with `showCloseButton={false}`.
 *
 * When `secondRow` is provided, the header renders a second, full-width row
 * beneath the first — for record headers whose chip row shares the top row
 * with actions+close while the identity block spans the full width below.
 */
const Header = ({
  className,
  children,
  onClose,
  actions,
  showCloseButton = true,
  align = 'center',
  secondRow,
  ...props
}: SlideoutHeaderProps) => {
  const surface = useSurfaceDismiss();
  const handleClose = onClose ?? surface?.close;
  const showClose = showCloseButton && handleClose !== undefined;
  const hasActions = isRenderableNode(actions);
  const hasSecondRow = isRenderableNode(secondRow);
  const actionsCluster =
    hasActions || showClose ? (
      <div className='flex shrink-0 items-center gap-1'>
        {actions}
        {showClose && (
          <CloseButton
            size='sm'
            onClick={handleClose}
            data-slideout-dismiss=''
          />
        )}
      </div>
    ) : null;

  if (hasSecondRow) {
    return (
      <header
        {...props}
        className={cx(
          'relative z-1 flex w-full flex-col px-4 pt-6 pb-6 md:px-6',
          className
        )}
      >
        <div
          className={cx(
            'flex w-full justify-between gap-2',
            align === 'start' ? 'items-start' : 'items-center'
          )}
        >
          <div className='min-w-0 flex-1'>{children}</div>
          {actionsCluster}
        </div>
        <div className='w-full min-w-0'>{secondRow}</div>
      </header>
    );
  }

  return (
    <header
      {...props}
      className={cx(
        'relative z-1 flex w-full justify-between gap-2 px-4 pt-6 pb-6 md:px-6',
        align === 'start' ? 'items-start' : 'items-center',
        className
      )}
    >
      <div className='min-w-0 flex-1'>{children}</div>
      {actionsCluster}
    </header>
  );
};
Header.displayName = 'SlideoutHeader';

const Footer = (props: ComponentPropsWithRef<'footer'>) => {
  return (
    <footer
      {...props}
      className={cx(
        'w-full p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-[inset_0px_1px_0px_0px] shadow-border-secondary md:px-6',
        props.className
      )}
    />
  );
};
Footer.displayName = 'SlideoutFooter';

const SlideoutMenu = Menu as typeof Menu & {
  Trigger: typeof AriaDialogTrigger;
  Content: typeof Content;
  Header: typeof Header;
  Footer: typeof Footer;
};
SlideoutMenu.displayName = 'SlideoutMenu';

SlideoutMenu.Trigger = AriaDialogTrigger;
SlideoutMenu.Content = Content;
SlideoutMenu.Header = Header;
SlideoutMenu.Footer = Footer;

export { SlideoutMenu };
